import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Button,
  Autocomplete,
  TextField,
  Tooltip,
  CircularProgress,
  Grid,
  IconButton,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ROUTE_CANVISION_VIDEO_WALL } from 'constants/routerPath.js';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import TuneIcon from '@mui/icons-material/Tune';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { useCANvisionContext } from '../../context/useCANvisionContext';
import { syncSiteCameras } from 'api/canvms';

// ---------------------------------------------------------------------------
// Placeholder command center options (UI-only — no API wiring yet)
// ---------------------------------------------------------------------------
interface CommandCenterOption {
  id: number;
  name: string;
}

const COMMAND_CENTER_OPTIONS: CommandCenterOption[] = [
  { id: 1, name: 'Main Command Center' },
  { id: 2, name: 'Regional Command Center' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ControlPanelProps {
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Left-side control panel with command center / site selection and management buttons.
 *
 * Contains:
 *  - Command Center Autocomplete dropdown (from context)
 *  - Site Autocomplete dropdown with inline Refresh button (from context)
 *  - 2x2 button grid: Manage Sites, Manage Sets, Camera Settings, Sync Cameras
 *  - Full-width "Video Wall" navigation button
 */
export function ControlPanel({ sx }: ControlPanelProps) {
  const navigate = useNavigate();

  const {
    sites,
    sitesLoading,
    selectedSiteId,
    setSelectedSiteId,
    selectedCommandCenterId,
    setSelectedCommandCenterId,
    refetchSites,
    refetchCameras,
    setSiteManagerOpen,
    setCameraSetManagerOpen,
    setCameraSettingsOpen,
  } = useCANvisionContext();

  // -- Notification provider (global toast) ----------------------------------
  const { notifySuccess, notifyError } = useNotificationProvider();

  // -- Local sync state -----------------------------------------------------
  const [syncing, setSyncing] = useState(false);

  const selectedSite = sites.find((s) => s.id === selectedSiteId) ?? null;
  const selectedCommandCenter =
    COMMAND_CENTER_OPTIONS.find((cc) => cc.id === selectedCommandCenterId) ?? null;

  // -- Handlers -------------------------------------------------------------

  const handleCommandCenterChange = useCallback(
    (_: unknown, value: CommandCenterOption | null) => {
      setSelectedCommandCenterId(value?.id ?? null);
    },
    [setSelectedCommandCenterId],
  );

  const handleRefreshSites = useCallback(() => {
    refetchSites();
  }, [refetchSites]);

  const handleSiteChange = useCallback(
    (_: unknown, value: (typeof sites)[number] | null) => {
      setSelectedSiteId(value?.id ?? null);
    },
    [setSelectedSiteId],
  );

  const handleSync = useCallback(async () => {
    if (!selectedSiteId) return;

    try {
      setSyncing(true);
      const response = await syncSiteCameras(selectedSiteId);
      notifySuccess(`Synced ${response.data.synced} cameras from VMS`);
      await refetchCameras();
    } catch (err: any) {
      console.error('[ControlPanel] Sync cameras failed:', err);
      notifyError(err.response?.data?.message || 'Failed to sync cameras');
    } finally {
      setSyncing(false);
    }
  }, [selectedSiteId, refetchCameras, notifySuccess, notifyError]);

  // -- Render ---------------------------------------------------------------

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ...sx,
      }}
    >
      {/* Command Center selector */}
      <Autocomplete
        options={COMMAND_CENTER_OPTIONS}
        getOptionLabel={(option) => option.name}
        value={selectedCommandCenter}
        onChange={handleCommandCenterChange}
        size="small"
        onKeyDown={(event) => {
          if (event.key === 'Escape') event.stopPropagation();
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Command Center"
            placeholder="Choose a command center..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <BusinessIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 20 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Site selector with inline refresh button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Autocomplete
          options={sites}
          getOptionLabel={(option) => option.display_name || option.name}
          value={selectedSite}
          onChange={handleSiteChange}
          loading={sitesLoading}
          size="small"
          sx={{ flex: 1 }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') event.stopPropagation();
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Site"
              placeholder="Choose a site..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <LocationOnIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 20 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {sitesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Tooltip title="Refresh sites">
          <IconButton
            size="small"
            onClick={handleRefreshSites}
            sx={{ color: 'text.secondary' }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 2x2 button grid */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => setSiteManagerOpen(true)}
          >
            Manage Sites
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<ViewComfyIcon />}
            onClick={() => setCameraSetManagerOpen(true)}
          >
            Manage Sets
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<TuneIcon />}
            onClick={() => setCameraSettingsOpen(true)}
          >
            Camera Settings
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Tooltip
            title="Pending VMS migration"
            placement="top"
          >
            {/* Wrap in span so Tooltip works on disabled button */}
            <span>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<SyncIcon />}
                disabled
              >
                Sync Cameras
              </Button>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Full-width Video Wall button */}
      <Button
        fullWidth
        variant="contained"
        startIcon={<VideoLibraryIcon />}
        onClick={() => navigate(ROUTE_CANVISION_VIDEO_WALL)}
      >
        Video Wall
      </Button>
    </Paper>
  );
}

export default ControlPanel;
